import { merge } from 'lodash-es';
import { getColorStatusRendererMeta } from '../../../components/Form/ColorStatus/ColorStatus';
import { mapRenderTypeToPrimitive } from '../CustomFields/hooks/useCustomFieldsActions';
import { RendererType } from '../../../data-models/field-definition';
import {
  FormOptions,
  FundReportingGridRef,
  GridRef,
  IField,
  ISimpleChoice,
  createFieldDataModel,
  createFieldFormConfigDataModel,
  BasicFormConfig,
  rendererToFormatterId,
  rendererTypeToPrimitiveType,
} from '../../../data-models/field2.data-model';
import { createFormatterDataModel } from '../../../data-models/formatter.data-model';
import {
  CustomFieldFormType,
  CustomFieldFormViewModel,
  customFieldViewModelFormSchema,
} from './CustomField.schema';

/** Re-exported from its new home in field2.data-model, where the override gate also needs it. */
export { rendererToFormatterId };

export function createInnerViewModel(
  overrides: Partial<CustomFieldFormViewModel> = {}
): CustomFieldFormViewModel {
  return merge(customFieldViewModelFormSchema().getDefault(), overrides);
}

export function createCustomFieldViewModel(
  overrides: Partial<CustomFieldFormType> = {}
): CustomFieldFormType {
  return merge(
    {
      ...createFieldDataModel(),
      _viewModel: createInnerViewModel(overrides._viewModel),
    },
    overrides
  ) as CustomFieldFormType;
}

export function toFieldViewModel<T>(field: IField<T>): CustomFieldFormType {
  const formRefSet = new Set(field.systemMeta?.formRefs);
  const gridRefSet = new Set(field.systemMeta?.gridRefs);
  return {
    ...(field as Omit<CustomFieldFormType, '_viewModel'>),
    _viewModel: {
      rendererType: field.formMeta?.renderer?.type as RendererType,
      currencyConfig: field?.formMeta?.formatter?.config?.currency as string | undefined,
      // A composed currency formatter keeps the shape in `extends`; the code is the Currency
      // field's business, so Format shows the shape either way.
      formatterId: field.formMeta?.formatter?.extends || field.formMeta?.formatter?.id,
      usePresetOptions:
        field.formMeta?.renderer?.type === RendererType.multiSelect && hasPresetOptions(field),
      selectConfigValues: (field?.formMeta?.renderer?.config?.values as ISimpleChoice<string>[])?.map(
        (opt) => opt.value
      ),
      [FundReportingGridRef]: gridRefSet.has(FundReportingGridRef as GridRef),
      [GridRef.kpiDataCollection]: gridRefSet.has(GridRef.kpiDataCollection),
      [GridRef.portfolioReporting]: gridRefSet.has(GridRef.portfolioReporting),
      [GridRef.roundTracker]: gridRefSet.has(GridRef.roundTracker),
      [GridRef.securityOverview]: gridRefSet.has(GridRef.securityOverview),
      addingCaptableSecurity: formRefSet.has(FormOptions.addingCaptableSecurity),
      addingDeal: formRefSet.has(FormOptions.addingDeal),
      addingRound: formRefSet.has(FormOptions.addingRound),
      addingTransaction: formRefSet.has(FormOptions.addingTransaction),
      companyLists: formRefSet.has(FormOptions.companyLists),
      companyProfile: formRefSet.has(FormOptions.companyProfile),
      cleanupRemovedOptions: true,
    },
  };
}

export function fromFieldViewModel(field: CustomFieldFormType & IField<unknown>): IField<unknown> {
  const { _viewModel, ...fieldWithoutViewModel } = field;
  const res = {
    ...fieldWithoutViewModel,
    formMeta: merge(createFieldFormConfigDataModel(), {
      renderer: {
        id: _viewModel.rendererType ?? RendererType.text,
        type: _viewModel.rendererType,
        config: {
          values: _viewModel.selectConfigValues?.map((value) => ({
            displayName: value,
            value,
          })),
          allowCustomAdd: isFreeSoloMultiSelect(_viewModel) || undefined,
        },
      },
      formatter: buildCustomFieldFormatter(_viewModel),
    }),
    systemMeta: {
      formRefs: getFormRefs(_viewModel),
      gridRefs: getGridRefs(_viewModel),
      source: field.systemMeta?.source,
    },
    type: mapRenderTypeToPrimitive(_viewModel.rendererType),
  } as IField<unknown>;

  if (res.formMeta?.renderer?.type === RendererType.colorStatus) {
    res.formMeta.renderer.config = getColorStatusRendererMeta() as unknown as BasicFormConfig;
  }

  if (_viewModel.cleanupRemovedOptions != null) {
    (res as unknown as Record<string, unknown>).cleanupRemovedOptions = _viewModel.cleanupRemovedOptions;
  }

  return res;
}

/**
 * The formatter to persist for a custom field.
 *
 * Currency is the awkward case: `FormatterService` resolves a registered id straight from its
 * registry and never looks at the field's own config, so storing `currencyShort` with
 * `config.currency: 'EUR'` would render USD. Composing a per-currency id that `extends` the
 * chosen shape is what makes the code apply — the same trick `getFormatterModelForField3`
 * already uses for the derived currency formatters.
 *
 * With no chosen shape, the stored formatter is exactly what it was before this ticket.
 */
export function buildCustomFieldFormatter(viewModel: CustomFieldFormViewModel) {
  const derivedId = rendererToFormatterId(viewModel.rendererType);
  const shapeId = viewModel.formatterId || derivedId;
  const config = { currency: viewModel.currencyConfig };

  if (viewModel.rendererType === RendererType.currency && viewModel.currencyConfig && shapeId !== derivedId) {
    return createFormatterDataModel({
      id: `${viewModel.currencyConfig}-${shapeId}`,
      extends: shapeId,
      // Required, not cosmetic: this id is not registered, so the service builds it by merging
      // this model over the base it extends — and `createFormatterDataModel` defaults `type` to
      // 'string', which would win that merge and stringify the value instead of formatting it.
      type: rendererTypeToPrimitiveType(viewModel.rendererType),
      config,
    });
  }

  return createFormatterDataModel({ id: shapeId, config });
}

function isFreeSoloMultiSelect(viewModel: CustomFieldFormViewModel): boolean {
  return (
    viewModel.rendererType === RendererType.multiSelect &&
    (!viewModel.selectConfigValues || viewModel.selectConfigValues.length === 0)
  );
}

function hasPresetOptions<T>(field: IField<T>): boolean {
  const values = field?.formMeta?.renderer?.config?.values as ISimpleChoice<string>[] | undefined;
  return (values?.length ?? 0) > 0;
}

export function getGridRefs(viewModel: CustomFieldFormViewModel): GridRef[] {
  const result: GridRef[] = [];

  Object.values(GridRef).forEach((ref) => {
    if (viewModel[ref]) {
      result.push(ref);
    }
  });

  // Not an enum member, so the loop above can't reach it.
  if (viewModel[FundReportingGridRef]) {
    result.push(FundReportingGridRef as GridRef);
  }

  return result;
}

export function getFormRefs(viewModel: CustomFieldFormViewModel): FormOptions[] {
  const result: FormOptions[] = [];

  Object.keys(FormOptions).forEach((ref) => {
    if (viewModel[ref as keyof typeof viewModel]) {
      result.push(FormOptions[ref as keyof typeof FormOptions]);
    }
  });

  return result;
}
